import type { ReactNode } from 'react'
import './StateCard.css'

export interface StateCardProps {
  /** Icon + text; rendered in .card-header h3. */
  title: ReactNode
  /** Right side of the header (captions, toggles). */
  headerExtra?: ReactNode
  /** Appended to "card" on the root element. */
  className?: string
  /** Rendered inside the card, after the header and before the ladder.
   * Always shown — never gated by isLoading/isError/isEmpty — for content
   * that must keep the card's chrome (e.g. PicksPanel's stat tiles) while
   * still being visible through every ladder state, including loading. */
  preBody?: ReactNode
  isLoading: boolean
  isError: boolean
  errorText?: string
  isEmpty: boolean
  /** Custom empty node (e.g. PicksPanel's three reasons). Defaults to
   * rendering emptyIcon/emptyText in the standard empty-state markup. */
  empty?: ReactNode
  emptyText?: string
  emptyIcon?: ReactNode
  /** The loaded body. */
  children: ReactNode
}

/**
 * Shared card shell + three-state ladder (P8's loading/error/empty pattern),
 * extracted from BookValueChart/FleetValueOverlay's copy-pasted markup so a
 * future chart doesn't have to copy it a third time. The ladder markup below
 * is lifted verbatim from that pattern so migrating a component to StateCard
 * doesn't shift a pixel.
 *
 * State precedence: loading > error > empty > children. `preBody` sits
 * outside that ladder entirely — it always renders, alongside whichever
 * ladder rung is active.
 */
export function StateCard({
  title,
  headerExtra,
  className,
  preBody,
  isLoading,
  isError,
  errorText = 'unavailable',
  isEmpty,
  empty,
  emptyText,
  emptyIcon,
  children,
}: StateCardProps) {
  return (
    <div className={className ? `card ${className}` : 'card'}>
      <div className="card-header">
        <h3 className="card-title">{title}</h3>
        {headerExtra}
      </div>

      {preBody}

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isError ? (
        <div className="empty-state">
          <div className="empty-icon">{emptyIcon}</div>
          <div className="empty-text">{errorText}</div>
        </div>
      ) : isEmpty ? (
        empty ?? (
          <div className="empty-state">
            <div className="empty-icon">{emptyIcon}</div>
            <div className="empty-text">{emptyText}</div>
          </div>
        )
      ) : (
        children
      )}
    </div>
  )
}
